import React, { useCallback, useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { Box, Divider, Typography } from '@material-ui/core';

import { HoldingPage } from '@vyce/core/src/components/HoldingPage';
import { NO_MODULE_ACCESS_TEXT } from '@vyce/core/src/constants';
import config from '@vyce/core/src/assets/config';
import { FormConfirmSubscriptionDialog } from '@vyce/core/src/views/forms/components/FormConfirmSubscriptionDialog';

import { useSubscriptionData } from '../../../modules/subscriptionInfoModule/hooks/useSubscriptionData';

interface Props {
  hadSubscription?: boolean;
  hasPermissions?: boolean;
  hasSubscription?: boolean;
}

export const GetStarted: React.FC<Props> = ({ hadSubscription, hasPermissions, hasSubscription }) => {
  const history = useHistory();
  const {
    openFormsSubscriptionDialog,
    closeFormsSubscriptionDialog,
    addFormsSubscription,
    isFormsSubscriptionDialogOpen,
    loadingFormsSubscription,
    formsSubscription,
  } = useSubscriptionData();

  const textInsteadButton = hasPermissions ? '' : NO_MODULE_ACCESS_TEXT;
  const buttonText = useMemo(() => {
    if (hadSubscription) return 'Reactivate your Forms Module now...';
    if (!hasSubscription || !formsSubscription) return 'Activate your Forms Module now...';
    return "Let's start";
  }, [hadSubscription, hasSubscription, formsSubscription]);

  const goToSubscription = () => {
    history.push('/subscriptions?dialog=true');
  };

  const subscribeToForms = () => {
    addFormsSubscription();
  };

  const handleClick = useCallback(() => {
    if (hadSubscription || !hasSubscription) {
      return goToSubscription();
    }
    if (hasSubscription && !formsSubscription) {
      openFormsSubscriptionDialog();
    }
  }, [hadSubscription, formsSubscription, hasSubscription, addFormsSubscription]);

  return (
    <>
      <FormConfirmSubscriptionDialog
        open={isFormsSubscriptionDialogOpen}
        setOpen={closeFormsSubscriptionDialog}
        confirmFunction={subscribeToForms}
        loading={loadingFormsSubscription}
      />
      <HoldingPage
        title="Forms"
        buttonText={buttonText}
        handleButtonClick={handleClick}
        textInsteadButton={textInsteadButton}
        image={config.welcome.forms}>
        <Box marginBottom={4}>
          <Box marginTop={1} display="flex">
            <Typography>Collect insights. Share results. Stay on top of progress.</Typography>
          </Box>

          <Box marginTop={3}>
            <Typography style={{ fontWeight: 600 }}>Create flexible forms with ease</Typography>
          </Box>

          <Box marginTop={1}>
            <Typography>
              From quick polls to detailed surveys, choose from single or multiple choice, scales, dates,
              times, or open answers. Add images or videos for extra context.
            </Typography>
          </Box>

          <Divider style={{ margin: '8px 0' }} />

          <Box marginTop={2}>
            <Typography style={{ fontWeight: 600 }}>Track and analyze responses</Typography>
          </Box>

          <Box marginTop={1}>
            <Typography>
              Instantly see each completed form, review insights with clear diagrams and share them with
              other admins, so they can take action.
            </Typography>
          </Box>

          <Divider style={{ margin: '8px 0' }} />

          <Box marginTop={2}>
            <Typography style={{ fontWeight: 600 }}>Access anytime, anywhere</Typography>
          </Box>

          <Box marginTop={1}>
            <Typography>
              Admins and workers alike can open all their forms anytime, whether they’re waiting to be filled
              out or already submitted.
            </Typography>
          </Box>
        </Box>
      </HoldingPage>
    </>
  );
};
